import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../config/supabase';

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout
const CHECK_INTERVAL = 60 * 1000; // Check every minute

export const useSessionTimeout = () => {
  const { session, signOut } = useAuth();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  // Update last activity timestamp - always define but only execute with session
  const updateActivity = useCallback(() => {
    if (session) {
      lastActivityRef.current = Date.now();
    }
  }, [session]);

  // Handle session timeout - always define but only execute with session
  const handleSessionTimeout = useCallback(async () => {
    if (!session) {
      return; // Early return if no session, but don't skip hook definition
    }

    try {
      await signOut();
      alert('Your session has expired due to inactivity. Please log in again.');
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during session timeout:', error);
      window.location.href = '/login';
    }
  }, [session, signOut]);

  // Reset timeout timers - always define
  const resetTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }

    if (session) {
      // Set warning timer (25 minutes)
      warningRef.current = setTimeout(() => {
        showTimeoutWarning();
      }, SESSION_TIMEOUT - WARNING_TIME);

      // Set timeout timer (30 minutes)
      timeoutRef.current = setTimeout(() => {
        handleSessionTimeout();
      }, SESSION_TIMEOUT);
    }
  }, [session]);

  // Show session timeout warning - always define
  const showTimeoutWarning = useCallback(() => {
    if (!session) {
      return; // Early return if no session, but don't skip hook definition
    }

    const confirmed = window.confirm(
      'Your session will expire in 5 minutes due to inactivity. Click OK to continue your session or Cancel to log out now.'
    );
    
    if (confirmed) {
      // User wants to continue - reset timers
      resetTimers();
      updateActivity();
    } else {
      // User chose to log out
      handleSessionTimeout();
    }
  }, [session, resetTimers, updateActivity, handleSessionTimeout]);

  // Check for server-side session expiration - always define
  const checkServerSession = useCallback(async () => {
    if (!session) {
      return; // Early return if no session, but don't skip hook definition
    }

    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error || !currentSession) {
        console.log('Server session expired');
        await handleSessionTimeout();
        return;
      }

      // Make a test API call to check if session is still valid
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/auth/verify-session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        const data = await response.json();
        if (data.code === 'SESSION_EXPIRED' || data.code === 'SESSION_TIMEOUT') {
          console.log('Session expired on server');
          await handleSessionTimeout();
        }
      }
    } catch (error) {
      console.error('Error checking server session:', error);
      // Don't show any popups for API errors when there might be no session
    }
  }, [session, handleSessionTimeout]);

  // Activity event handlers
  const handleActivity = useCallback(() => {
    if (session) {
      updateActivity();
      resetTimers();
    }
  }, [session, updateActivity, resetTimers]);

  useEffect(() => {
    if (!session) {
      // Clear all timers when not logged in
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      return;
    }

    // Start timers for logged in user
    resetTimers();

    // Set up server session check interval
    checkIntervalRef.current = setInterval(checkServerSession, CHECK_INTERVAL);

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [session, resetTimers, handleActivity, checkServerSession]);

  return {
    updateActivity,
    resetTimers
  };
};
